"use client"

import React from "react"
import { useState, useEffect } from "react"
import dayjs, { Dayjs } from "dayjs"
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonRefresher, IonRefresherContent } from "@ionic/react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  TablePagination,
  LinearProgress,
  Snackbar,
  TableFooter,
  Avatar,
} from "@mui/material"
import {
  Search,
  FilterList,
  Download,
  Visibility,
  Close,
  AttachMoney,
  Receipt,
  Person,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Info as InfoIcon,
  Business,
  Category,
  ClearAll,
  Refresh,
} from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import axios from "axios"

import { fetchOrderDetails_items, fetchSalesData_withdate_staffId_paymentMethodCall, fetchSalesData_withdate_staffId_receptionist } from "../../../api/reports/reports"
import { formatCurrencyN } from "../../../common/utils_t"
import { getTlocations_production_centre } from "../../../api/stock-transfer/stock-transfer"
import { getCategories } from "../../../api/products/product"
import { MenuButton } from "../../../components/Menu"
import { baseUrl, config, requestID } from "../../../common/utils"

// Define interfaces for your API response
interface ApiOrderItem {
  id: number;
  order_id: string;
  product_id: string;
  quantity: number;
  price: string;
  total_amount: string;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  status: string;
  last_updated: string;
  category_id: string;
  branch_id: string;
  business_id: string;
  description: string;
  cost_price: string;
  member_id: string;
  date_bags: string;
  date_bags_copy: string;
  checked_in: number;
  checked_out: number;
  month_id: string;
  roleID: string;
  check_in_date: string;
  check_out_date: string;
  checked_out_by: string;
  reversed_at: string;
  reversed_by: string;
  name: string;
}

interface ApiOrderDetailsResponse {
  status: boolean;
  message: string;
  data: {
    items: ApiOrderItem[];
    count: number;
    date_range: {
      applied: boolean;
      start_date: string;
      end_date: string;
    };
  };
}

interface SalesTransaction {
  order_id: string
  order_code: string
  order_date: string
  mode_of_payment: string
  payable_amount: string
  balance_due: string
  order_by: string
  order_status: string
  member_id: string
  member_name: string
  payment_status: string
  otp_status: string | null
  items: any[]
  tabBalance: number
  debtBalance: number
  walletDetails: number
  walletDetails_l: number
  amount_paid: string
  staffFullName?: string
  firstName?: string
  branch_id?: string
  transaction_category?: string
  receptionist_id?: string
  receptionist_name?: string
}

interface Staff {
  staff_id: string
  staff_name: string
  user_id: string
  firstName: string
  lastName: string
  roleName: string
  branchName: string
  roleID?: string
  role?: string
  position?: string
  designation?: string
  user_type?: string
}

interface FilterState {
  startDate: Dayjs | null
  endDate: Dayjs | null
  staffId: string
}

 

// API function to fetch receptionists from backend
const getStaffList_saleapp = async () => {
  const data = {
    requestID: requestID(),
  };
  try {
    const response = await axios.post(
      baseUrl() + "businesss/getAllStaffs_saleapp",
      data,
      config
    );
    console.log("Staff response:", response);
    return response.data;
  } catch (error) {
    console.error("Error fetching staff:", error);
    throw error;
  }
};

const ReceptionistSalesReport: React.FC = () => {
  const [transactions, setTransactions] = useState<SalesTransaction[]>([])
  const [filteredTransactions, setFilteredTransactions] = useState<SalesTransaction[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showTransactionDetail, setShowTransactionDetail] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<SalesTransaction | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)

  const [staffList, setStaffList] = useState<Staff[]>([])
  const [loadingStaff, setLoadingStaff] = useState(false)

  const [filters, setFilters] = useState<FilterState>({
    startDate: dayjs().subtract(7, 'day'),
    endDate: dayjs(),
    staffId: "",
  })

  const [loadingDetails, setLoadingDetails] = useState(false)
  const [orderDetails, setOrderDetails] = useState<ApiOrderDetailsResponse['data'] | null>(null)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [dataLoaded, setDataLoaded] = useState(false)

  // Fetch receptionists data
  const fetchReceptionists = async () => {
    try {
      setLoadingStaff(true)
      const response = await getStaffList_saleapp()
      if (response.status === true) {
        // Filter for receptionists (roleID === 33 or role === 'receptionist')
        const staffList = response.data || []
        const receptionistList = staffList.filter(staff => 
          staff.roleID === '33' || 
          staff.roleID === 33 ||
          staff.role === 'receptionist' || 
          staff.position === 'receptionist' ||
          staff.designation === 'receptionist' ||
          staff.user_type === 'receptionist'
        )
        setStaffList(receptionistList)
        console.log("Receptionists loaded:", receptionistList.length)
      } else {
        setSnackbarMessage("Failed to load receptionists.")
        setSnackbarOpen(true)
      }
    } catch (error) {
      console.error("Error fetching receptionists:", error)
      setSnackbarMessage("Failed to load receptionists.")
      setSnackbarOpen(true)
    } finally {
      setLoadingStaff(false)
    }
  }

  const formatDateYMD = (date: Dayjs | null) => {
    if (!date || !date.isValid()) {
      return dayjs().format('YYYY-MM-DD')
    }
    return date.format('YYYY-MM-DD')
  }

  // Fetch sales data for specific receptionist and date range
  const fetchSalesDataByReceptionist = async () => {
    if (!filters.staffId || !filters.startDate || !filters.endDate) {
      setSnackbarMessage("Please select receptionist and date range")
      setSnackbarOpen(true)
      return
    }

    setLoading(true)
    setDataLoaded(false)
    
    const formattedStart = formatDateYMD(filters.startDate)
    const formattedEnd = formatDateYMD(filters.endDate)
    
    console.log("Fetching sales data for receptionist:", {
      staffId: filters.staffId,
      startDate: formattedStart,
      endDate: formattedEnd
    })

    try {
      // Using existing endpoint that supports staff ID filtering
      const response = await fetchSalesData_withdate_staffId_receptionist(
        rowsPerPage,
        formattedStart,
        formattedEnd,
        filters.staffId,
        "all", // payment method
        "" // branchId (empty for all branches)
      )
      
      console.log("Sales data response:", response)

      if (response.status === true) {
        const transactions = response.data.orders || []
        
        // Filter transactions to only include those with matching receptionist_id
        const filteredByReceptionist = transactions.filter((txn: SalesTransaction) => 
          txn.receptionist_id === filters.staffId || 
          txn.order_by === filters.staffId
        )
        
        setTransactions(filteredByReceptionist)
        setFilteredTransactions(filteredByReceptionist)
        setDataLoaded(true)
        
        setSnackbarMessage(`Loaded ${filteredByReceptionist.length} transactions`)
        setSnackbarOpen(true)
      } else {
        throw new Error(response.message || "Failed to load sales data")
      }
    } catch (error) {
      console.error("Error fetching sales data:", error)
      setSnackbarMessage("Failed to load sales data")
      setSnackbarOpen(true)
      setTransactions([])
      setFilteredTransactions([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch order details from API endpoint
  const fetchOrderDetails = async (orderId: string, branchId: string) => {
    setLoadingDetails(true)
    setDetailsError(null)
    setOrderDetails(null)

    try {
      const startDateStr = formatDateYMD(filters.startDate)
      const endDateStr = formatDateYMD(filters.endDate)

      const response = await fetchOrderDetails_items(orderId, startDateStr, endDateStr, branchId)

      if (response.status) {
        setOrderDetails(response.data)
        setSnackbarMessage("Order details loaded successfully")
        setSnackbarOpen(true)
      } else {
        throw new Error(response.data.message || "Failed to load order details")
      }

    } catch (error) {
      console.error("Error fetching order details:", error)
      setDetailsError(error instanceof Error ? error.message : "Failed to load order details")
      setSnackbarMessage("Failed to load order details")
      setSnackbarOpen(true)
    } finally {
      setLoadingDetails(false)
    }
  }

  useEffect(() => {
    fetchReceptionists()
  }, [])

  // Apply search filter
  useEffect(() => {
    if (!dataLoaded) return
    
    let filtered = transactions

    if (searchQuery) {
      filtered = filtered.filter(
        (transaction) =>
          transaction.order_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction.member_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction.order_id.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    }

    setFilteredTransactions(filtered)
  }, [transactions, searchQuery, dataLoaded])

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const clearFilters = () => {
    setFilters({
      startDate: dayjs().subtract(7, 'day'),
      endDate: dayjs(),
      staffId: "",
    })
    setSearchQuery("")
    setTransactions([])
    setFilteredTransactions([])
    setDataLoaded(false)
  }

  const viewTransactionDetail = async (transaction: SalesTransaction) => {
    setSelectedTransaction(transaction)
    setShowTransactionDetail(true)
    
    await fetchOrderDetails(transaction.order_id, transaction.branch_id || "")
  }

  const closeTransactionDetail = () => {
    setShowTransactionDetail(false)
    setSelectedTransaction(null)
    setOrderDetails(null)
    setDetailsError(null)
  }

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === "string" ? Number.parseFloat(amount) : amount
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(numAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const exportData = () => {
    if (filteredTransactions.length === 0) {
      setSnackbarMessage("No data to export")
      setSnackbarOpen(true)
      return
    }

    const headers = [
      "Order Code",
      "Date",
      "Receptionist",
      "Customer Name",
      "Payment Method",
      "Amount Paid",
      "Balance Due",
      "Order Status",
      "Receptionist ID",
      "Product Category",
    ]

    const csvContent = [
      headers.join(","),
      ...filteredTransactions.map((transaction) =>
        [
          transaction.order_code,
          transaction.order_date,
          `"${transaction.receptionist_name || transaction.staffFullName || 'N/A'}"`,
          `"${transaction.member_name}"`,
          transaction.mode_of_payment,
          transaction.amount_paid,
          transaction.balance_due,
          transaction.order_status,
          transaction.receptionist_id || transaction.order_by,
          transaction.transaction_category || "N/A",
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `receptionist-sales-report-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  // Calculate totals
  const calculateTotals = () => {
    const totals = {
      totalSales: 0,
      totalAmountPaid: 0,
      totalBalanceDue: 0,
      totalTransactions: filteredTransactions.length
    }

    filteredTransactions.forEach(transaction => {
      totals.totalSales += Number.parseFloat(transaction.payable_amount) || 0
      totals.totalAmountPaid += Number.parseFloat(transaction.amount_paid) || 0
      totals.totalBalanceDue += Number.parseFloat(transaction.balance_due) || 0
    })

    return totals
  }

  const getFullName = (staff: Staff) => {
    return `${staff.firstName || ''} ${staff.lastName || ''}`.trim()
  }

  const totals = calculateTotals()

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <IonPage className="pushDown">
        <IonHeader>
          <IonToolbar>
            <MenuButton />
            <IonTitle>Receptionist Sales Report</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          <Box sx={{ p: 2 }}>
            {/* Filter Panel */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Filter Transactions
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={filters.startDate}
                    onChange={(date) => handleFilterChange("startDate", date)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={filters.endDate}
                    onChange={(date) => handleFilterChange("endDate", date)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Receptionist</InputLabel>
                    <Select
                      value={filters.staffId}
                      label="Select Receptionist"
                      onChange={(e) => handleFilterChange("staffId", e.target.value)}
                      disabled={loadingStaff}
                      startAdornment={
                        loadingStaff ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null
                      }
                    >
                      <MenuItem value="">
                        <Typography sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.9rem" }}>
                          Select a receptionist
                        </Typography>
                      </MenuItem>
                      {staffList.map((staff) => (
                        <MenuItem
                          key={staff.user_id}
                          value={staff.user_id}
                          sx={{
                            py: 1.2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: "0.9rem",
                              bgcolor: "primary.main",
                            }}
                          >
                            {staff.firstName?.charAt(0) || 'R'}
                            {staff.lastName?.charAt(0) || ''}
                          </Avatar>
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, lineHeight: 1.2 }}>
                              <Typography sx={{ fontWeight: 600 }}>
                                {getFullName(staff)}
                              </Typography>
                              <Chip 
                                label="Receptionist" 
                                size="small" 
                                color="primary" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              ID: {staff.user_id} • {staff.branchName || 'N/A'}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchSalesDataByReceptionist}
                    disabled={loading || !filters.staffId || !filters.startDate || !filters.endDate}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                  >
                    {loading ? "Loading..." : "Generate Report"}
                  </Button>
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="outlined" 
                  startIcon={<ClearAll />} 
                  onClick={clearFilters}
                  size="small"
                >
                  Clear All
                </Button>
                
                {dataLoaded && (
                  <Button 
                    variant="outlined" 
                    startIcon={<Download />} 
                    onClick={exportData}
                    size="small"
                    disabled={filteredTransactions.length === 0}
                  >
                    Export CSV
                  </Button>
                )}
                
                {filters.staffId && (
                  <Chip
                    label={`Selected: ${staffList.find(s => s.user_id === filters.staffId)?.firstName || filters.staffId}`}
                    onDelete={() => handleFilterChange("staffId", "")}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Paper>

            {/* Summary Cards - Only show when data is loaded */}
            {dataLoaded && filteredTransactions.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: "primary.light", color: "white" }}>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <Receipt sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                        Total Transactions
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                        {totals.totalTransactions}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: "success.light", color: "white" }}>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <AttachMoney sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                        Total Sales
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                        {formatCurrencyN(totals.totalSales)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: "info.light", color: "white" }}>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                        Amount Paid
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                        {formatCurrencyN(totals.totalAmountPaid)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: "warning.light", color: "white" }}>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <Warning sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                        Balance Due
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                        {formatCurrencyN(totals.totalBalanceDue)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Search Box - Only show when data is loaded */}
            {dataLoaded && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <TextField
                    fullWidth
                    placeholder="Search by order code, customer name, or order ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {filteredTransactions.length} transactions found
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Transactions Table */}
            {dataLoaded ? (
              filteredTransactions.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.100" }}>
                        <TableCell>Order Code</TableCell>
                        <TableCell>Date</TableCell>
                                                <TableCell>Staff Full Name</TableCell>

                        <TableCell>Customer</TableCell>
                        <TableCell>Payment Method</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Balance</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTransactions
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((transaction) => (
                          <TableRow
                            key={transaction.order_id}
                            hover
                            sx={{ cursor: "pointer" }}
                            onClick={() => viewTransactionDetail(transaction)}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                {transaction.order_code}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {transaction.order_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(transaction.order_date)}
                              </Typography>
                            </TableCell>

                            
                               <TableCell>
                              <Typography variant="body2">
                                {transaction.staffFullName || transaction.staffFullName || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {transaction.member_name || transaction.firstName || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={transaction.mode_of_payment} 
                                size="small" 
                                color="secondary" 
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(transaction.amount_paid)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography 
                                variant="body2" 
                                color={Number.parseFloat(transaction.balance_due) > 0 ? "error.main" : "success.main"}
                                fontWeight="bold"
                              >
                                {formatCurrency(transaction.balance_due)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={transaction.order_status}
                                size="small"
                                color={
                                  transaction.order_status === 'COMPLETED' ? 'success' :
                                  transaction.order_status === 'PENDING' ? 'warning' : 'error'
                                }
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    viewTransactionDetail(transaction)
                                  }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Page Totals
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {formatCurrency(
                              filteredTransactions
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .reduce((sum, txn) => sum + Number.parseFloat(txn.amount_paid), 0)
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {formatCurrency(
                              filteredTransactions
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .reduce((sum, txn) => sum + Number.parseFloat(txn.balance_due), 0)
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={filteredTransactions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(Number.parseInt(e.target.value, 10))
                      setPage(0)
                    }}
                  />
                </TableContainer>
              ) : (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No transactions found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No transactions match your current filters. Try adjusting your criteria.
                  </Typography>
                </Paper>
              )
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading transactions...</Typography>
              </Box>
            ) : (
              <Paper sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Data Loaded
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please select a receptionist and date range, then click "Generate Report" to load transactions.
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Transaction Detail Dialog */}
          <Dialog 
            open={showTransactionDetail} 
            onClose={closeTransactionDetail} 
            maxWidth="md" 
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {loadingDetails ? "Loading..." : `Order - ${selectedTransaction?.order_code}`}
                </Typography>
                <IconButton onClick={closeTransactionDetail}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            
            {loadingDetails && <LinearProgress />}
            
            <DialogContent dividers>
              {loadingDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading details...</Typography>
                </Box>
              ) : detailsError ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                  <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography color="error" gutterBottom>
                    Error Loading Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    {detailsError}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => selectedTransaction && fetchOrderDetails(selectedTransaction.order_id, selectedTransaction.branch_id || "")}
                    sx={{ mt: 2 }}
                  >
                    Retry
                  </Button>
                </Box>
              ) : orderDetails ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Order Items ({orderDetails.items.length})
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderDetails.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography fontWeight="medium">{item.name}</Typography>
                              {item.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {item.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                            <TableCell align="right" fontWeight="bold">
                              {formatCurrency(item.total_amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
              ) : (
                <Typography color="text.secondary" align="center" sx={{ p: 3 }}>
                  No order details available
                </Typography>
              )}
            </DialogContent>
            
            <DialogActions>
              <Button onClick={closeTransactionDetail}>Close</Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
          />
        </IonContent>
      </IonPage>
    </LocalizationProvider>
  )
}

export default ReceptionistSalesReport